/*
 * Cloud-sky scroll-scrub.
 * Drives the hero #cloud-sky-hero-video <video>'s currentTime from the
 * scroll position of the .adm-banner-section hero. Uses GSAP ScrollTrigger
 * for smoothed scrub. Loads GSAP defensively so it cohabits with other
 * sections on the page that already load it (e.g., gsap-split-text).
 */
(function () {
  var GSAP_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.15/dist/gsap.min.js';
  var ST_SRC = 'https://cdn.jsdelivr.net/npm/gsap@3.15/dist/ScrollTrigger.min.js';

  // How many times the video loops as the hero scrolls past the viewport.
  // 1 = one full playthrough from hero entering bottom to hero leaving top.
  var LOOP_COUNT = 1;

  // Smoothing factor for the rAF interpolator (0..1). Lower = smoother but
  // laggier; higher = more responsive but jerkier. 0.12 reads as buttery.
  var SMOOTHING = 0.12;

  // GSAP scrub easing in seconds. Higher = smoother scroll-following.
  var SCRUB = 1.2;

  function loadScript(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === '1') return resolve();
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', reject);
        return;
      }
      var s = document.createElement('script');
      s.src = src;
      s.async = false;
      s.addEventListener('load', function () { s.dataset.loaded = '1'; resolve(); });
      s.addEventListener('error', reject);
      document.head.appendChild(s);
    });
  }

  function ensureGsap() {
    var p = window.gsap ? Promise.resolve() : loadScript(GSAP_SRC);
    return p.then(function () {
      return window.ScrollTrigger ? Promise.resolve() : loadScript(ST_SRC);
    });
  }

  function whenMetadata(video) {
    return new Promise(function (resolve) {
      if (video.readyState >= 1 && video.duration && isFinite(video.duration)) {
        resolve();
      } else {
        video.addEventListener('loadedmetadata', function once() {
          video.removeEventListener('loadedmetadata', once);
          resolve();
        });
      }
    });
  }

  // iOS Safari sometimes refuses to seek a muted video until it has been
  // "started" once. play()+pause() right after metadata is the standard
  // unlock; if it rejects (no gesture), we retry on the first user input.
  function unlockSeek(video) {
    var unlocked = false;
    function attempt() {
      if (unlocked) return;
      var p = video.play();
      if (p && typeof p.then === 'function') {
        p.then(function () {
          video.pause();
          video.currentTime = 0;
          unlocked = true;
        }).catch(function () {
          // Wait for a gesture.
          var onGesture = function () {
            window.removeEventListener('pointerdown', onGesture);
            window.removeEventListener('touchstart', onGesture);
            attempt();
          };
          window.addEventListener('pointerdown', onGesture, { once: true });
          window.addEventListener('touchstart', onGesture, { once: true });
        });
      } else {
        try { video.pause(); } catch (e) {}
        unlocked = true;
      }
    }
    attempt();
  }

  function init() {
    var hero = document.querySelector('.adm-banner-section');
    var video = document.getElementById('cloud-sky-hero-video');
    if (!hero || !video) return;

    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) {
      // Hide the video; let the gradient fallback stand alone.
      video.style.display = 'none';
      return;
    }

    ensureGsap().then(function () {
      if (!window.gsap || !window.ScrollTrigger) return;
      gsap.registerPlugin(ScrollTrigger);

      whenMetadata(video).then(function () {
        unlockSeek(video);

        var duration = video.duration;
        if (!duration || !isFinite(duration)) return;

        // Two-stage smoothing: GSAP scrub eases scroll → proxy.time, then a
        // rAF interpolator eases proxy.time → video.currentTime. Combined,
        // this kills both per-frame scroll jitter and per-seek video chop.
        var proxy = { time: 0 };
        var rendered = 0;
        var rafId = null;

        function shortestDelta(from, to, span) {
          var d = (to - from) % span;
          if (d > span / 2) d -= span;
          if (d < -span / 2) d += span;
          return d;
        }

        function tick() {
          var target = proxy.time % duration;
          if (target < 0) target += duration;
          // Interpolate along the shortest wrap-aware path so a loop boundary
          // doesn't trigger a full reverse-seek.
          var delta = shortestDelta(rendered, target, duration);
          rendered += delta * SMOOTHING;
          rendered = ((rendered % duration) + duration) % duration;
          try { video.currentTime = rendered; } catch (e) {}
          rafId = requestAnimationFrame(tick);
        }

        gsap.to(proxy, {
          time: LOOP_COUNT * duration,
          ease: 'none',
          scrollTrigger: {
            trigger: hero,
            start: 'top bottom',
            end: 'bottom top',
            scrub: SCRUB,
            onToggle: function (self) {
              // Only burn rAF cycles while the hero is in/near view.
              if (self.isActive && rafId === null) {
                rendered = (proxy.time % duration + duration) % duration;
                rafId = requestAnimationFrame(tick);
              } else if (!self.isActive && rafId !== null) {
                cancelAnimationFrame(rafId);
                rafId = null;
              }
            }
          }
        });
        // Kick off rAF in case the hero is already in view at load.
        rendered = 0;
        rafId = requestAnimationFrame(tick);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
